﻿import { toast } from "sonner";
import type { ApiProblem, UploadKind, UploadResponse } from "@ticketnjoy/types";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from "@ticketnjoy/types";

import i18n from "@/lib/i18n";

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5258";

const LOCALE_COOKIE = "NEXT_LOCALE";

/**
 * Browser cookie'sinden (document.cookie) NEXT_LOCALE'i oku.
 * Server component'te çalışmaz — SSR için locale fetch'leri next-intl'in
 * getLocale() ile yapılmalı ve header opsiyonunda override edilmeli.
 */
function readLocaleFromCookie(): Locale {
  if (typeof document === "undefined") return DEFAULT_LOCALE;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]+)`),
  );
  const value = match?.[1];
  return value && (SUPPORTED_LOCALES as readonly string[]).includes(value)
    ? (value as Locale)
    : DEFAULT_LOCALE;
}

/**
 * Backend ProblemDetails (RFC 7807) response'u ile eşleşen error sınıfı.
 * UI katmanında code'a göre mesaj gösterebilir.
 *
 * `traceId`: backend her hata yanıtına isteğin iz kimliğini (32 hex) extension olarak yazar.
 * 5xx hatalarda kullanıcıya destek talebi için kopyalanabilir kod olarak gösterilir.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public detail: string,
    public fieldErrors?: Record<string, string[]>,
    public traceId?: string,
  ) {
    super(detail || code);
    this.name = "ApiError";
  }
}

export function isNotFoundError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/**
 * Hata response'unu ApiError'a dönüştürür.
 * Backend ProblemDetails extensions altında traceId + errors taşıdığı için
 * burayı tek noktada parse ediyoruz; tüm fetch katmanları aynı şekilde ele alır.
 */
export function problemToApiError(status: number, payload: unknown): ApiError {
  const problem = (payload ?? {}) as ApiProblem;
  const code = problem.title ?? codeForEmptyBody(status);
  return new ApiError(
    status,
    code,
    localizedDetail(code, problem.detail),
    problem.errors,
    problem.traceId,
  );
}

// Rate limiter ve rol yetkilendirmesi gövdesiz yanıt döner.
function codeForEmptyBody(status: number): string {
  if (status === 429) return "rate_limited";
  if (status === 403) return "forbidden";
  return "unknown_error";
}

/**
 * Backend `detail`'i sabit Türkçe ve bazen dinamik değer taşıyor (bakiye, adet):
 * Türkçede aynen gösterilir. Diğer dillerde kod → `apiError.codes.*`; listede
 * olmayan kod genel mesaja düşer. Yeni backend kodu dashboard'a çıkacaksa üç
 * mesaj dosyasına eklenmeli.
 */
function localizedDetail(code: string, detail: string | undefined): string {
  if (detail && i18n.resolvedLanguage === "tr") return detail;
  const key = `apiError.codes.${code}`;
  if (/^[a-z0-9_]+$/.test(code) && i18n.exists(key)) return i18n.t(key);
  return i18n.t("common.unexpectedError");
}

/**
 * Generic API hata gösterici. Tüm catch blokları bunu çağırır → tutarlı UX.
 *
 * - 4xx (kullanıcı kaynaklı): sadece `detail` toast'ı (aktif dilde, `localizedDetail`).
 * - 5xx (sunucu hatası): `detail` + traceId description + "Kodu Kopyala" action.
 *   Kullanıcı support'a paylaştığında biz Cloud Run logs'ta direkt arayabiliyoruz.
 * - Bilinmeyen / network: fallback mesaj.
 *
 * Kullanım:
 *   try { await mutate(); } catch (err) { showApiError(err); }
 */
export function showApiError(
  err: unknown,
  fallback = i18n.t("apiError.generic"),
): void {
  if (!(err instanceof ApiError)) {
    toast.error(fallback);
    return;
  }

  if (err.status >= 500 && err.traceId) {
    toast.error(err.detail || fallback, {
      description: i18n.t("apiError.supportCode", { code: err.traceId }),
      action: {
        label: i18n.t("apiError.copyCode"),
        onClick: () => {
          navigator.clipboard?.writeText(err.traceId ?? "").catch(() => {
            // Clipboard erişimi reddedilirse sessizce yut — toast zaten görünür.
          });
        },
      },
      duration: 10000,
    });
    return;
  }

  toast.error(err.detail || fallback);
}

export interface ApiClientOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

/**
 * API istek helper'ı. Cookie'yi otomatik gönderir (credentials: include).
 * JSON body'yi serialize eder, response'u parse eder, hataları ApiError'a dönüştürür.
 */
export async function apiClient<T = unknown>(
  path: string,
  options: ApiClientOptions = {},
): Promise<T> {
  const { body, headers, ...rest } = options;

  // Backend middleware X-Locale header'ını ilk kaynak olarak kullanıyor;
  // cross-origin cookie'ler gelmeyebileceği için explicit gönderiyoruz.
  const locale = readLocaleFromCookie();

  const init: RequestInit = {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "X-Locale": locale,
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...rest,
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, init);

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json") ||
                 contentType.includes("application/problem+json");

  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw problemToApiError(response.status, payload);
  }

  return payload as T;
}

/**
 * Görsel upload — backend /organizer/uploads endpoint'ine multipart POST atar.
 * Ownership kontrolü server'da; client sadece kind + ilgili id'yi gönderir.
 *
 * JSON wrapper'ı (`apiClient`) kullanmıyoruz çünkü multipart'da Content-Type otomatik
 * set edilmeli (browser boundary'yi kendi ekler). Error shape aynı — ApiError fırlatılır.
 */
export async function uploadImage(params: {
  file: File;
  kind: UploadKind;
  eventId?: string;
  shopId?: string;
  signal?: AbortSignal;
  onProgress?: (pct: number) => void;
}): Promise<UploadResponse> {
  const { file, kind, eventId, shopId, signal, onProgress } = params;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("kind", kind);
  if (eventId) formData.append("eventId", eventId);
  if (shopId) formData.append("shopId", shopId);

  const locale = readLocaleFromCookie();

  // Progress gerekiyorsa XHR, yoksa fetch. Fetch'in Response.body ReadableStream progress
  // Chrome'da upload tarafı için hâlâ sınırlı; XHR daha portable.
  if (onProgress) {
    return await new Promise<UploadResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_BASE_URL}/organizer/uploads`);
      xhr.withCredentials = true;
      xhr.setRequestHeader("Accept", "application/json");
      xhr.setRequestHeader("X-Locale", locale);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => {
        const contentType = xhr.getResponseHeader("content-type") ?? "";
        const isJson =
          contentType.includes("application/json") ||
          contentType.includes("application/problem+json");
        const payload = isJson && xhr.responseText
          ? JSON.parse(xhr.responseText)
          : xhr.responseText;
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(payload as UploadResponse);
        } else {
          reject(problemToApiError(xhr.status, payload));
        }
      };
      xhr.onerror = () =>
        reject(new ApiError(0, "network_error", i18n.t("apiError.network")));
      xhr.onabort = () =>
        reject(new ApiError(0, "aborted", i18n.t("apiError.uploadAborted")));
      signal?.addEventListener("abort", () => xhr.abort());
      xhr.send(formData);
    });
  }

  const response = await fetch(`${API_BASE_URL}/organizer/uploads`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "X-Locale": locale },
    body: formData,
    signal,
  });

  const contentType = response.headers.get("content-type") ?? "";
  const isJson =
    contentType.includes("application/json") ||
    contentType.includes("application/problem+json");
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw problemToApiError(response.status, payload);
  }

  return payload as UploadResponse;
}
